import { PageTitle } from '@/components/nav/PageTitle';
import { useModal } from '@/components/modals/Modal';
import { useState } from 'react';
import { SearchIcon } from '@/components/icons';
import { useTranslation } from '@/hooks/useTranslation';
import { LoaderFullscreen } from '@/components/layout/Loader';
import { Breadcrumb } from '@/components/nav/Breadcrumb';
import { TabNavigationLayout } from '@/components/generic/nav/TabNavigationLayout';
import { ADMIN_TABS, useAdminTabs } from '../useAdminTabs';
import { AdminHero } from '../AdminHero';
import { Tag } from '@/components/badges/Tag';
import { RegisterNewProModal } from '@/components/admin/company/RegisterNewProModal';
import consultingCompanyApi from '@/slices/consultingCompany/consultingCompany.api';
import enterpriseApi from '@/slices/enterprises/enterprise.api';
import accountApi from '@/slices/account/account.api';
import { useSelector } from 'react-redux';
import { selectHydratedConsultingCompanies } from '@/slices/consultingCompany/consultingCompany.selector';
import { CompanyItem } from './CompanyItem';

export const AdminCompaniesScreen = () => {
  const { t } = useTranslation();

  const { isLoading: consultingCompaniesLoading } =
    consultingCompanyApi.endpoints.getConsultingCompanies.useQuery();
  const { isLoading: enterprisesLoading } =
    enterpriseApi.endpoints.getEnterprises.useQuery();
  const { isLoading: usersLoading } = accountApi.endpoints.getUsers.useQuery();

  const hydratedConsultingCompanies = useSelector(
    selectHydratedConsultingCompanies,
  );

  const modal = useModal();
  const adminTabs = useAdminTabs();

  const [searchTerm, setSearchTerm] = useState('');

  const openRegisterNewProModal = () => {
    modal.openModal(<RegisterNewProModal />, {
      title: 'Ajouter un pro',
    });
  };

  if (consultingCompaniesLoading || enterprisesLoading || usersLoading) {
    return <LoaderFullscreen />;
  }

  const normalizedSearchTerm = searchTerm.toLowerCase();
  const filteredCompanies = hydratedConsultingCompanies.filter((company) => {
    const enterpriseConcerned = company.projectsResolved.some((enterprise) =>
      enterprise.name.toLowerCase().includes(normalizedSearchTerm),
    );

    const employeeConcerned = company.employeesResolved.some((employee) =>
      `${employee.firstName} ${employee.lastName}`
        .toLowerCase()
        .includes(normalizedSearchTerm),
    );

    return (
      company.name.toLowerCase().includes(normalizedSearchTerm) ||
      enterpriseConcerned ||
      employeeConcerned
    );
  });

  return (
    <>
      <Breadcrumb />
      <TabNavigationLayout<ADMIN_TABS>
        tabs={adminTabs}
        activeTab={ADMIN_TABS.CONSULTING_COMPANIES}
        renderTabs={(tabsContent) => <AdminHero>{tabsContent}</AdminHero>}
      >
        <PageTitle title="Admin" />
        <div className="w-full main-content p-8">
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h1>{t('admin:companies.list.title')}</h1>
                <Tag color={'blue'}>{hydratedConsultingCompanies.length}</Tag>
              </div>
              <button
                className="primary purple"
                onClick={openRegisterNewProModal}
              >
                {t('global:add')}
              </button>
            </div>
            <form className="w-full">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center ps-3 pointer-events-none">
                  <SearchIcon />
                </div>
                <input
                  type="search"
                  id="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('admin:companies.list.searchPlaceholder')}
                  className="block w-full p-4 ps-10 sami-body-small text-gray-900 border border-gray-300 rounded-lg focus:ring-purple-500"
                />
              </div>
            </form>
            <ul className="space-y-4">
              {filteredCompanies.map((company, index) => (
                <CompanyItem company={company} index={index} key={company.id} />
              ))}
            </ul>
          </div>
        </div>
      </TabNavigationLayout>
    </>
  );
};
